import { useState } from "react";
import type { PartyCard } from "../../lib/parties";
import { useEdgeFade } from "../../lib/useEdgeFade";

// Compact party chip row (Round 10, 2A): replaces the parties grid. One
// horizontal row of chips (role icon + canonical name) sorted by fact
// count descending, overflow behind "+N more". Identity/context, not a
// content section — renders directly under the case header.

const ROLE_ICONS: Record<string, string> = {
  "Law enforcement": "👮",
  Government: "🏛️",
  Insurance: "🛡️",
  "Tow company": "🚛",
  "Rental car provider": "🚗",
  "Vehicle service": "🔧",
  Retailer: "🛒",
};

const MAX_VISIBLE = 6;

export default function PartyChips({
  cards,
  factCounts,
  onOpen,
}: {
  cards: PartyCard[];
  // canonical name → attributed fact count (0 for correspondent-only).
  factCounts: Map<string, number>;
  onOpen: (card: PartyCard) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Edge fade re-evaluates when expanding changes the row's scrollWidth.
  const rowRef = useEdgeFade<HTMLDivElement>(
    `${expanded}:${cards.length}`,
  );
  if (cards.length === 0) return null;

  const sorted = [...cards].sort(
    (a, b) =>
      (factCounts.get(b.name) ?? 0) - (factCounts.get(a.name) ?? 0) ||
      a.name.localeCompare(b.name),
  );
  const visible = expanded ? sorted : sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - MAX_VISIBLE;

  return (
    <div className="party-chip-row h-scroll-fade" aria-label="Parties" ref={rowRef}>
      {visible.map((c) => (
        <button
          key={c.name}
          type="button"
          className="party-chip"
          title={[c.org, c.role].filter(Boolean).join(" · ") || c.name}
          onClick={() => onOpen(c)}
        >
          <span aria-hidden>{(c.role && ROLE_ICONS[c.role]) || "👤"}</span>{" "}
          {c.name}
        </button>
      ))}
      {!expanded && overflow > 0 && (
        <button
          type="button"
          className="party-chip party-chip-more"
          onClick={() => setExpanded(true)}
        >
          +{overflow} more
        </button>
      )}
      {expanded && overflow > 0 && (
        <button
          type="button"
          className="party-chip party-chip-more"
          onClick={() => setExpanded(false)}
        >
          show fewer
        </button>
      )}
    </div>
  );
}
